/**
 * Os componentes da casca de navegação de verdade (das áreas internas e do
 * portal público), como a aba Navegação os
 * descreve.
 *
 * A casca (`CascaDeArea`, `BarraLateral`, `BuscaGlobal`, `MenuDaConta`,
 * `Migalhas`) não monta dentro da documentação: ela pede a sessão do `useAuth`,
 * a rota da área e um `SidebarProvider` próprio, e a barra lateral é `fixed` —
 * montada aqui dentro, ela cobriria a própria página. Por isso a aba mostra a
 * estrutura e esta tabela, e monta ao vivo só o que monta sozinho. Nenhuma
 * maquete se apresenta como componente real.
 *
 * `AdminDesignSystem.spec.tsx` confere cada linha contra o arquivo: o
 * componente é exportado ali com esse nome, e cada prop listada existe na
 * assinatura.
 */

export interface ComponenteDaCasca {
  nome: string;
  /** Caminho relativo a `apps/web/src`. */
  arquivo: string;
  /** Nome de cada prop, com `?` quando opcional. Vazio = sem props. */
  props: readonly string[];
  descricao: string;
}

export const CASCA_REAL: readonly ComponenteDaCasca[] = [
  {
    nome: 'CascaDeArea',
    arquivo: 'nucleo/navegacao/CascaDeArea.tsx',
    props: ['area'],
    descricao:
      'Casca das áreas internas (/operacao, /admin, /sme, /dre): barra lateral, cabeçalho, migalhas e o <main id="conteudo"> onde a tela entra.',
  },
  {
    nome: 'BarraLateral',
    arquivo: 'nucleo/navegacao/BarraLateral.tsx',
    props: ['area'],
    descricao:
      'Menu da área, gerado de nucleo/navegacao/mapa.ts (a mesma fonte das rotas). Grupos recolhíveis guardados por área; item de fase futura com marcador de construção.',
  },
  {
    nome: 'BuscaGlobal',
    arquivo: 'nucleo/navegacao/BuscaGlobal.tsx',
    props: ['area'],
    descricao:
      'Busca por tela da área, aberta pelo botão ou por Ctrl+K / ⌘K. Esc fecha e devolve o foco a quem abriu.',
  },
  {
    nome: 'Notificacoes',
    arquivo: 'nucleo/navegacao/Notificacoes.tsx',
    props: ['contagem?'],
    descricao:
      'Sino do cabeçalho, com a contagem de não lidas vinda de quem o monta. É o gatilho da GavetaDeNotificacoes.',
  },
  {
    nome: 'Migalhas',
    arquivo: 'nucleo/navegacao/Migalhas.tsx',
    props: ['raizDaArea', 'grupo?', 'item', 'extra?'],
    descricao:
      'Trilha "onde estou": raiz da área, grupo do menu, item e, quando a tela define, um terceiro segmento (aba ou registro aberto).',
  },
  {
    nome: 'MenuDaConta',
    arquivo: 'nucleo/navegacao/MenuDaConta.tsx',
    props: ['usuario', 'areaAtual', 'aoSair'],
    descricao:
      'Quem está conectado e com que perfil (do GET /eu); "Meu perfil" e "Preferências de notificação" no mesmo lugar em todas as áreas, logo abaixo; atalhos para as outras áreas que o usuário alcança e Sair. Sem troca de perfil.',
  },
  {
    nome: 'SkipToContent',
    arquivo: 'ui/SkipToContent.tsx',
    props: [],
    descricao:
      'Primeiro elemento focável da página: "Pular para o conteúdo principal", que leva a #conteudo. Invisível até receber foco.',
  },
  {
    nome: 'ThemeToggle',
    arquivo: 'ui/ThemeToggle.tsx',
    props: [],
    descricao: 'Seletor de tema Claro / Escuro / Sistema (next-themes, chave teg-theme).',
  },
  {
    nome: 'MobileNavMenu',
    arquivo: 'ui/MobileNavMenu.tsx',
    props: ['title?', 'backTo', 'backLabel?', 'links?', 'menuLabel?', 'menuAriaLabel?'],
    descricao:
      'Barra compacta [Voltar] [título] [⋯] das telas de detalhe abaixo de 640 px, com alvo de toque de 44 px.',
  },
  {
    nome: 'AcessibilidadeWidget',
    arquivo: 'ui/AcessibilidadeWidget.tsx',
    props: [],
    descricao:
      'Botão flutuante do portal público: tamanho da fonte, alto contraste, movimento reduzido e links sublinhados, guardados neste navegador.',
  },
  {
    nome: 'CookieConsent',
    arquivo: 'ui/CookieConsent.tsx',
    props: [],
    descricao:
      'Faixa do portal público avisando que só há cookies essenciais, com o link para o aviso de privacidade. É aviso, não coleta de consentimento; some ao ser fechada e a escolha fica neste navegador.',
  },
];
